import { useCallback } from 'react';
import { toast } from 'react-toastify';

import { IAbsenceRequestResultDTO, ICreateAbsenceRequestDTO, IEmployeeBalanceDTO, IHolidayDTO } from 'src/api/dtos';
import { IError } from 'src/api/types';
import { useThunkDispatch } from 'src/shared/hooks';
import { useTranslate } from 'src/shared/providers';
import { getErrorMessage, isOfType, mapCreateAbsenceRequest } from 'src/shared/utils';
import { createAbsence } from 'src/store/absence/absences.actions';

interface IAbsenceRequestSubmitProps {
  onOpenModal: (modal: IHolidayDTO | undefined) => void;
  inputs: Partial<ICreateAbsenceRequestDTO>;
  onSuccess: () => void;
  onBalanceUpdate: (balance: IEmployeeBalanceDTO | undefined) => void;
}

export const useAbsenceRequestSubmit = ({
  inputs,
  onSuccess,
  onBalanceUpdate,
  onOpenModal,
}: IAbsenceRequestSubmitProps) => {
  const dispatchThunk = useThunkDispatch();
  const { t } = useTranslate();

  const submitForm = useCallback(
    async (ignoreStatOverlap = false) => {
      try {
        const payload = mapCreateAbsenceRequest(inputs);
        const response = await dispatchThunk(createAbsence({ ...payload, ignoreStatOverlap: !!ignoreStatOverlap }));

        // Check if there's an overlapping holiday
        if (response.overlappingHoliday && !ignoreStatOverlap) {
          onOpenModal(response.overlappingHoliday);
          return;
        }
        onOpenModal(undefined);
        onSuccess();
        onBalanceUpdate(response.employeeBalance || undefined);
      } catch (e: any) {
        const { data } = e;

        // Violated Employee Balance use case
        if (isOfType<IAbsenceRequestResultDTO>(data, 'employeeBalance') && data.employeeBalance) {
          const { employeeBalance } = data;
          onBalanceUpdate(employeeBalance);
          return;
        }

        const error = e as IError;
        const message = getErrorMessage(error, t('absences.oopsAnErrorOccurredWhileValidatingYourInputs'));
        toast.info(message, { type: toast.TYPE.ERROR });
        return;
      }
    },
    [inputs, onOpenModal],
  );

  return { submitForm };
};
